import { useMemo } from 'react';
import { checkPermissionForFeature } from '../utils/checkPermission';

export type PermissionType =
  | 'viewPermission'
  | 'editPermission'
  | 'addPermission'
  | 'deletePermission';

interface UsePermissionsResult {
  canView: boolean;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  hasAnyPermission: boolean;
  hasAllPermissions: boolean;
  isReadOnly: boolean;
  checkPermission: (permissionType: PermissionType) => boolean;
}

/**
 * Custom hook to check permissions for a specific screen
 * @param screenId - The backend screen ID (e.g., 'backend.clients')
 * @returns Object with permission flags and helper functions
 *
 * @example
 * const { canView, canEdit, isReadOnly } = usePermissions('backend.clients');
 *
 * if (isReadOnly) {
 *   // Render view-only UI
 * }
 */
export const usePermissions = (screenId: string): UsePermissionsResult => {
  const permissions = useMemo(() => {
    const canView = checkPermissionForFeature(screenId, 'viewPermission');
    const canEdit = checkPermissionForFeature(screenId, 'editPermission');
    const canAdd = checkPermissionForFeature(screenId, 'addPermission');
    const canDelete = checkPermissionForFeature(screenId, 'deletePermission');

    return {
      canView,
      canEdit,
      canAdd,
      canDelete,
      hasAnyPermission: canView || canEdit || canAdd || canDelete,
      hasAllPermissions: canView && canEdit && canAdd && canDelete,
      isReadOnly: canView && !canEdit && !canAdd && !canDelete,
      checkPermission: (permissionType: PermissionType) =>
        checkPermissionForFeature(screenId, permissionType),
    };
  }, [screenId]);

  return permissions;
};

/**
 * Hook for checking multiple screen permissions at once
 * @param screenIds - Array of screen IDs
 * @returns Map of screen IDs to their permissions
 *
 * @example
 * const permissions = useMultiplePermissions(['backend.clients', 'backend.waiver_information']);
 * if (permissions['backend.clients'].canEdit) { ... }
 */
export const useMultiplePermissions = (
  screenIds: string[],
): Record<string, UsePermissionsResult> => {
  return useMemo(() => {
    const permissionsMap: Record<string, UsePermissionsResult> = {};

    for (const screenId of screenIds) {
      const canView = checkPermissionForFeature(screenId, 'viewPermission');
      const canEdit = checkPermissionForFeature(screenId, 'editPermission');
      const canAdd = checkPermissionForFeature(screenId, 'addPermission');
      const canDelete = checkPermissionForFeature(screenId, 'deletePermission');

      permissionsMap[screenId] = {
        canView,
        canEdit,
        canAdd,
        canDelete,
        hasAnyPermission: canView || canEdit || canAdd || canDelete,
        hasAllPermissions: canView && canEdit && canAdd && canDelete,
        isReadOnly: canView && !canEdit && !canAdd && !canDelete,
        checkPermission: (permissionType: PermissionType) =>
          checkPermissionForFeature(screenId, permissionType),
      };
    }

    return permissionsMap;
  }, [screenIds]);
};
